import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { Line } from "react-chartjs-2";
import { Chart as ChartJS, LineElement, CategoryScale, LinearScale, PointElement, Tooltip, Legend } from "chart.js";

import { useQuery } from "@/utils/query";
import { API } from "@/services/API";
import { useIsDarkTheme } from "@/components/layout/useIsDarkTheme";
import { TranslatedText } from "@/components/common/text";
import { InfoButton } from "@/components/common/buttons";
import { calculateMaxValue, calculateMinValue } from "@/utils/graph";
import { Skeleton } from "@/components/common/other/Skeleton";
import { cn } from "@/utils/css";
import { ErrorComponent } from "@/components/common/errors";
import { LineChartTooltip } from "@/components/dataPages";
import { today } from "@/utils/date";

ChartJS.register(LineElement, CategoryScale, LinearScale, PointElement, Tooltip, Legend);

export const RpmVsDistance = () => {
    const { pickupZip, pickupDate } = useQuery();
    const [labels, setLabels] = useState([]);
    const [data, setData] = useState([]);
    const isDarkTheme = useIsDarkTheme();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [tooltipData, setTooltipData] = useState({
        x: 0,
        y: 0,
        value: "",
        visible: false,
    });

    const externalTooltipHandler = (context) => {
        const { chart, tooltip } = context;
        if (!tooltip || tooltip.opacity === 0) {
            setTooltipData((prev) => (prev.visible ? { ...prev, visible: false } : prev));
            return;
        }

        const tooltipWidth = 60;
        const tooltipHeight = 30;

        const chartWidth = chart.width;
        const chartHeight = chart.height;

        let x = tooltip.caretX + 6 + 3; // 6 is hover radius, 3 is padding
        let y = tooltip.caretY - tooltipHeight / 2; // Center vertically

        // Shift left if too close to right edge
        if (x + tooltipWidth > chartWidth) {
            x = tooltip.caretX - tooltipWidth - 6 - 3;
        }

        // Shift up if too close to bottom edge
        if (y + tooltipHeight > chartHeight) {
            y = y - (y + tooltipHeight - chartHeight);
        }

        const label = tooltip.body?.[0]?.lines?.[0] || "";

        const newData = {
            x,
            y,
            value: label,
            visible: true,
        };

        setTooltipData((prev) => {
            if (prev.x !== newData.x || prev.y !== newData.y || prev.value !== newData.value || prev.visible !== newData.visible) {
                return newData;
            }
            return prev;
        });
    };

    const maxValue = useMemo(() => calculateMaxValue(data), [data]);
    const minValue = useMemo(() => calculateMinValue(data), [data]);
    const stepSize = 0.5;
    const minTickValue = useMemo(() => {
        const tempMin = Math.floor(minValue / stepSize) * stepSize;
        return tempMin < 0 ? 0 : tempMin;
    }, [minValue]);

    const maxTickValue = useMemo(() => Math.ceil(maxValue / stepSize) * stepSize, [maxValue]);

    useEffect(() => {
        if (pickupZip) {
            setLoading(true);
            setError(null);
            API.get("/analytics/rpm-vs-distance-distribution", {
                params: {
                    departure: pickupZip,
                    "for-date": pickupDate || today,
                    "start-date": dayjs(pickupDate || undefined)
                        .subtract(2, "month")
                        .format("YYYY-MM-DD"),
                },
            })
                .then((res) => {
                    const midpoints = res.distanceBins.slice(0, -1).map((v, i) => (v + res.distanceBins[i + 1]) / 2);
                    setLabels(midpoints.map((bin) => `${Math.round(bin)} mi`));
                    setData(res.rpm);
                })
                .catch((err) => {
                    console.error("Error fetching rpm vs distance", err);
                    setError(err);
                })
                .finally(() => setLoading(false));
        }
    }, [pickupZip, pickupDate]);

    const chartData = {
        labels,
        datasets: [
            {
                data,
                borderColor: "#24B98C",
                backgroundColor: "transparent",
                fill: true,
                pointRadius: 3,
                hoverRadius: 6,
                pointBackgroundColor: "#24B98C",
                pointHoverBackgroundColor: "#2BBC90",
                borderWidth: 2,
                pointBorderWidth: 3,
                pointHoverBorderColor: "#121815",
                pointBorderColor: "#24B98C",
                clip: false,
            },
        ],
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        height: 164,
        plugins: {
            datalabels: {
                display: false,
            },
            interaction: {
                mode: "nearest",
                intersect: false,
            },

            legend: {
                display: false,
            },

            title: {
                display: false,
            },

            tooltip: {
                mode: "nearest",
                intersect: false,
                enabled: false,
                external: externalTooltipHandler,
            },
        },
        hover: {
            mode: "nearest",
            intersect: false,
        },
        scales: {
            x: {
                title: {
                    display: false,
                },
                ticks: {
                    intersect: true,
                    font: {
                        size: 14,
                        family: "Saira",
                    },
                    crossAlign: "near",
                    color: isDarkTheme ? "#838A85" : "#979B98",
                },

                grid: {
                    display: false,
                },

                border: {
                    display: false,
                },
            },

            y: {
                display: true,
                title: {
                    display: false,
                },
                min: minTickValue,
                max: maxTickValue,
                stepSize,
                ticks: {
                    color: isDarkTheme ? "#838A85" : "#979B98",
                    padding: 5,
                    crossAlign: "far",
                    font: {
                        size: 14,
                        family: "Saira",
                    },
                    maxTicksLimit: 5,
                    callback: (value) => {
                        return new Intl.NumberFormat("en-US", { minimumFractionDigits: 1 }).format(value);
                    },
                },

                grid: {
                    display: true,
                    drawTicks: false,
                    color: isDarkTheme ? "#282928" : "#E5E9EB",
                    drawBorder: false,
                },

                border: {
                    display: false,
                },
            },
        },
        animation: {
            duration: 300,
            easing: "easeInOutQuad",
        },
    };

    return (
        <div className="w-full flex flex-col">
            <div className="text-cm-p-text-primary text-base/[25px] flex items-center mb-2">
                <TranslatedText>Outbound RPM - Distance</TranslatedText>
                <div className="ml-[10px]">
                    <InfoButton text="RPM vs distance" />
                </div>
            </div>
            <div className="h-[164px] w-full relative">
                <Skeleton
                    className={cn("min-h-full absolute top-0 left-0 right-0 bottom-0 opacity-50", {
                        hidden: !loading,
                    })}
                />
                <ErrorComponent
                    className={cn("min-h-full absolute top-0 left-0 right-0 bottom-0", {
                        hidden: !error,
                    })}
                />
                <div
                    className={cn("h-[164px]", {
                        hidden: loading || error,
                    })}
                >
                    <Line data={chartData} options={options} />
                </div>
                <LineChartTooltip {...tooltipData} />
            </div>
        </div>
    );
};
